import { useState, useEffect, useRef } from 'react';
import { groundsAPI, seriesAPI, divisionsAPI, conditionsAPI, teamsAPI } from '../../services/api';
import { PageHeader, SectionTitle, Field, Spinner, Modal, ConfirmDialog } from '../../components/ui';
import useAuthStore from '../../store/authStore';
import { SingleDatePicker } from '../../components/DateRangePicker';
import toast from 'react-hot-toast';

// ─── Constants ────────────────────────────────────────────────────────────────

const STATUS_COLORS = {
  available:  { bg: 'rgba(0,200,150,.18)',  border: 'rgba(0,200,150,.4)',  text: 'var(--accent)' },
  blocked:    { bg: 'rgba(239,68,68,.18)',   border: 'rgba(239,68,68,.4)',  text: 'var(--red)' },
  restricted: { bg: 'rgba(245,166,35,.18)',  border: 'rgba(245,166,35,.4)', text: 'var(--gold)' },
  none:       { bg: 'var(--card)',            border: 'var(--border)',        text: 'var(--dim)' },
};
const STATUS_CYCLE = ['none', 'available', 'blocked', 'restricted'];
const DAY_TO_DOW   = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };
const DOW_LABEL    = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];

// ─── Helpers ──────────────────────────────────────────────────────────────────

function getDatesForDows(start, end, allowedDows) {
  const dates = [];
  const parts = (s) => s.split('T')[0].split('-').map(Number);
  const [sy, sm, sd] = parts(start);
  const [ey, em, ed] = parts(end);
  const cur  = new Date(sy, sm - 1, sd);
  const endD = new Date(ey, em - 1, ed);
  while (cur <= endD) {
    if (allowedDows.has(cur.getDay())) dates.push(new Date(cur));
    cur.setDate(cur.getDate() + 1);
  }
  return dates;
}

function fmt(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

// Pool-aware match calculation
// For each pool: even → (g×t)/2, odd → (g×t-1)/2
// poolCounts = [{ pool:'A', count:11 }, { pool:'B', count:12 }]
// If no pools, treat whole division as one group
function computeMatchesForDivision(division, poolCountsMap) {
  const g = parseInt(division.games_per_team || 9, 10);
  const pools = poolCountsMap[division.id]; // array of { pool, count }

  if (!pools || pools.length === 0) {
    // No pool breakdown — use total team_count, standard formula
    const t = parseInt(division.team_count || 0, 10);
    if (t < 2) return { total: 0, breakdown: null };
    const total = Math.floor((g * t) / 2);
    return { total, breakdown: null };
  }

  let grandTotal = 0;
  const breakdown = [];

  pools.forEach(({ pool, count: t }) => {
    if (t < 2) { breakdown.push({ pool, matches: 0, note: `${t} team` }); return; }
    let matches, note;
    if (t % 2 === 0) {
      // Even pool — standard full round-robin estimate
      matches = (g * t) / 2;
      note = `${t} teams (even)`;
    } else {
      // Odd pool — Rank 1 gets bye on last game, rest play seeded matchups
      // (g-1) games for all teams + seeded game for remaining (t-1) teams
      const leagueGames = g - 1;
      const leagueMatches = (leagueGames * t) / 2;   // always integer since t is odd and (g-1) is even... check:
      // Actually: leagueGames*t may be odd. Use floor.
      const seededMatches = (t - 1) / 2;              // always integer since t is odd
      matches = Math.floor(leagueGames * t / 2) + seededMatches;
      note = `${t} teams (odd) — ${Math.floor(leagueGames * t / 2)} league + ${seededMatches} seeded`;
    }
    grandTotal += matches;
    breakdown.push({ pool, matches, note });
  });

  return { total: grandTotal, breakdown };
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function StatCard({ label, value, sub, accent, highlight, highlightColor, note, breakdown }) {
  return (
    <div style={{
      background: 'var(--card)', borderRadius: 10, padding: '14px 18px',
      borderLeft: highlight ? `4px solid ${highlightColor || 'var(--accent)'}` : '4px solid var(--border)',
      flex: 1, minWidth: 160,
      boxShadow: highlight ? `0 0 20px ${highlightColor || 'var(--accent)'}22` : 'none',
      transition: 'box-shadow .2s',
    }}>
      <div style={{ fontSize: 11, color: 'var(--mid)', fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 28, fontWeight: 800, color: accent || 'var(--text)', fontFamily: "'Roboto Mono', monospace", lineHeight: 1 }}>{value ?? '—'}</div>
      {sub && <div style={{ fontSize: 11, color: 'var(--mid)', marginTop: 4 }}>{sub}</div>}
      {breakdown && breakdown.length > 0 && (
        <div style={{ marginTop: 6, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {breakdown.map(b => (
            <span key={b.pool} style={{ fontSize: 10, background: 'rgba(100,149,237,.15)', color: 'var(--blue)', borderRadius: 4, padding: '2px 7px' }}>
              Pool {b.pool}: {b.matches}
            </span>
          ))}
        </div>
      )}
      {note && <div style={{ fontSize: 10, color: 'var(--dim)', marginTop: 6, lineHeight: 1.4, fontStyle: 'italic' }}>{note}</div>}
    </div>
  );
}


// ─── Main Component ───────────────────────────────────────────────────────────

export default function AvailabilityPage() {
  const { canEdit } = useAuthStore();

  const [seriesList,       setSeriesList]       = useState([]);
  const [divisions,        setDivisions]        = useState([]);
  const [grounds,          setGrounds]          = useState([]);
  const [blackouts,        setBlackouts]        = useState([]);
  const [groundRules,      setGroundRules]      = useState([]);
  const [teamGroundLimits, setTeamGroundLimits] = useState([]);
  const [groundBookings,   setGroundBookings]   = useState([]);
  // poolCountsMap: { divisionId: [{ pool:'A', count:11 }, ...] }
  const [poolCountsMap,    setPoolCountsMap]    = useState({});

  const [selSeries,    setSelSeries]    = useState('');
  const [selDivision,  setSelDivision]  = useState('');
  const [selGround,    setSelGround]    = useState('');

  const [playDates,     setPlayDates]    = useState([]);
  const [playDayLabels, setPlayDayLabels] = useState([]);

  // { groundId: { 'YYYY-MM-DD': status } }
  const [groundDateMaps, setGroundDateMaps] = useState({});
  const [loadedGrounds,  setLoadedGrounds]  = useState(new Set());
  const [loadingGround,  setLoadingGround]  = useState(false);
  const [saving,         setSaving]         = useState(false);
  const [dirty,          setDirty]          = useState(false);
  const [applyToAll,     setApplyToAll]     = useState(false);
  const [manualGames,    setManualGames]    = useState({});
  const [rulesLoading,   setRulesLoading]   = useState(false);
  const rulesRef = useRef(0);

  const [showBlackoutModal, setShowBlackoutModal] = useState(false);
  const [blackoutForm,      setBlackoutForm]      = useState({ start_date: '', end_date: '', reason: '' });
  const [deletingBlackout,  setDeletingBlackout]  = useState(null);

  // ── Initial loads ─────────────────────────────────────────────────────────

  useEffect(() => {
    loadSeries();
    loadGroundsInit();
  }, []);

  useEffect(() => {
    if (!selSeries) return;
    loadDivisions(selSeries);
    loadBlackouts(selSeries);
    loadAllConditions(selSeries);
    setGroundDateMaps({});
    setLoadedGrounds(new Set());
    setDirty(false);
  }, [selSeries]);

  useEffect(() => {
    if (selSeries && seriesList.length > 0) buildDates(selSeries);
  }, [selSeries, seriesList]);

  // When divisions load, fetch pool counts for each
  useEffect(() => {
    if (divisions.length > 0 && selSeries) loadPoolCounts(selSeries, divisions);
  }, [divisions]);

  // When BOTH series and grounds are ready, fetch ALL grounds' availability at once.
  // This ensures slot totals are accurate and data survives ground switching + page refresh.
  useEffect(() => {
    if (!selSeries || grounds.length === 0) return;
    fetchAllGroundsAvail(grounds, selSeries);
  }, [selSeries, grounds]);

  // ── Loaders ───────────────────────────────────────────────────────────────

  const loadSeries = async () => {
    try {
      const { data } = await seriesAPI.list();
      setSeriesList(data);
    } catch { toast.error('Failed to load series'); }
  };

  const loadDivisions = async (sid) => {
    try { const { data } = await divisionsAPI.list(sid); setDivisions(data); } catch {}
  };

  const loadGroundsInit = async () => {
    try {
      const { data } = await groundsAPI.list();
      setGrounds(data);
      if (data.length > 0) setSelGround(data[0].id);
    } catch { toast.error('Failed to load grounds'); }
  };

  const loadBlackouts = async (sid) => {
    try { const { data } = await groundsAPI.getBlackouts(sid); setBlackouts(data); } catch {}
  };

  const loadAllConditions = async (sid) => {
    setRulesLoading(true);
    const tok = ++rulesRef.current;
    try {
      const { data } = await conditionsAPI.list({ series_id: sid });
      if (tok !== rulesRef.current) return;
      const all = data || [];
      setGroundRules(all.filter(c => c.condition_type === 'ground_rule'));
      setTeamGroundLimits(all.filter(c => c.condition_type === 'team_ground_limit'));
      setGroundBookings(all.filter(c => c.condition_type === 'ground_booking'));
    } catch {} finally {
      if (tok === rulesRef.current) setRulesLoading(false);
    }
  };

  // Fetch teams for each division, group by pool, build poolCountsMap
  const loadPoolCounts = async (sid, divs) => {
    try {
      const { data: allTeams } = await teamsAPI.list({ series_id: sid });
      const map = {};
      divs.forEach(d => {
        const divTeams = allTeams.filter(t => t.division_id === d.id);
        // Group by pool value
        const poolGroups = {};
        divTeams.forEach(t => {
          const p = t.pool || '_none';
          if (!poolGroups[p]) poolGroups[p] = 0;
          poolGroups[p]++;
        });
        const pools = Object.entries(poolGroups)
          .filter(([p]) => p !== '_none')
          .map(([pool, count]) => ({ pool, count }))
          .sort((a, b) => a.pool.localeCompare(b.pool));
        // Only use pool breakdown if pools actually exist
        map[d.id] = pools.length > 0 ? pools : [];
      });
      setPoolCountsMap(map);
    } catch {}
  };

  const buildDates = (sid) => {
    const s = seriesList.find(x => x.id === sid);
    if (!s?.start_date || !s?.end_date) return;
    const rawDays = s.play_days || ['Sun'];
    const dows    = new Set(rawDays.map(d => DAY_TO_DOW[d]).filter(d => d !== undefined));
    setPlayDayLabels(rawDays);
    setPlayDates(getDatesForDows(s.start_date, s.end_date, dows));
  };

  // Fetch all grounds at once — parallel requests, single loading state.
  // Called whenever series changes so slot totals are always accurate.
  const fetchAllGroundsAvail = async (groundsList, sid) => {
    if (!sid || !groundsList.length) return;
    setLoadingGround(true);
    try {
      const results = await Promise.all(
        groundsList.map(g =>
          groundsAPI.getAvailability(g.id, { series_id: sid })
            .then(({ data }) => ({ gid: g.id, data }))
            .catch(() => ({ gid: g.id, data: {} }))
        )
      );
      const newMaps = {};
      const newLoaded = new Set();
      results.forEach(({ gid, data }) => {
        const rows = data.availability || (Array.isArray(data) ? data : []);
        const map  = {};
        rows.forEach(r => { map[String(r.available_date).split('T')[0]] = r.status; });
        newMaps[gid] = map;
        newLoaded.add(gid);
      });
      setGroundDateMaps(newMaps);
      setLoadedGrounds(newLoaded);
    } catch { toast.error('Failed to load ground availability'); }
    finally { setLoadingGround(false); }
  };

  // Still keep single-ground fetch for after-save refresh of one ground
  const fetchGroundAvail = async (gid) => {
    if (!selSeries) return;
    try {
      const { data } = await groundsAPI.getAvailability(gid, { series_id: selSeries });
      const rows = data.availability || (Array.isArray(data) ? data : []);
      const map  = {};
      rows.forEach(r => { map[String(r.available_date).split('T')[0]] = r.status; });
      setGroundDateMaps(p => ({ ...p, [gid]: map }));
      setLoadedGrounds(p => new Set([...p, gid]));
    } catch {}
  };

  // ── Date map helpers ──────────────────────────────────────────────────────

  const currentMap = groundDateMaps[selGround] || {};

  const updateMap = (gid, fn) => {
    setGroundDateMaps(p => ({ ...p, [gid]: fn(p[gid] || {}) }));
    setDirty(true);
  };

  const affectedGrounds = () => applyToAll ? grounds.map(g => g.id) : [selGround];

  const toggleDate = (dateStr) => {
    if (!canEdit()) return;
    const fn = (map) => {
      const cur  = map[dateStr] || 'none';
      const next = STATUS_CYCLE[(STATUS_CYCLE.indexOf(cur) + 1) % STATUS_CYCLE.length];
      return { ...map, [dateStr]: next };
    };
    affectedGrounds().forEach(gid => updateMap(gid, fn));
  };

  const bulkMark = (dow, status) => {
    if (!canEdit()) return;
    const fn = (map) => {
      const u = { ...map };
      playDates.forEach(d => { if (d.getDay() === dow) u[fmt(d)] = status; });
      return u;
    };
    affectedGrounds().forEach(gid => updateMap(gid, fn));
  };

  const bulkMarkAllDates = (status) => {
    if (!canEdit()) return;
    const fn = (map) => {
      const u = { ...map };
      playDates.forEach(d => { u[fmt(d)] = status; });
      return u;
    };
    affectedGrounds().forEach(gid => updateMap(gid, fn));
  };

  const clearAll = () => {
    if (!canEdit()) return;
    affectedGrounds().forEach(gid => updateMap(gid, () => ({})));
  };

  // ── Save (fixed) ──────────────────────────────────────────────────────────
  // Bug fix: was passing { available_date } but API util expects { date }
  // Now we pass dates directly in the correct { date, status } shape

  const saveAll = async () => {
    if (!selSeries)                return toast.error('Select a series first');
    if (!applyToAll && !selGround) return toast.error('Select a ground');
    setSaving(true);
    try {
      const toSave = affectedGrounds();
      for (const gid of toSave) {
        const map   = groundDateMaps[gid] || {};
        const dates = Object.entries(map)
          .filter(([, s]) => s && s !== 'none')
          .map(([date, status]) => ({ date, status })); // ← correct field name for API util
        // groundsAPI.saveAvailability expects records with series_id on first element
        // to extract. Pass correct shape:
        if (dates.length === 0) continue; // nothing to save for this ground
        await groundsAPI.saveAvailability(gid,
          dates.map(d => ({
            available_date: d.date,  // used by the util to extract into { date, status }
            status: d.status,
            series_id: selSeries,
            division_id: selDivision || null,
          }))
        );
      }
      toast.success(applyToAll ? `Saved ${toSave.length} grounds` : 'Availability saved');
      setDirty(false);
      // Re-fetch all grounds to confirm DB state matches UI
      fetchAllGroundsAvail(grounds, selSeries);
    } catch (err) { toast.error(err?.response?.data?.error || 'Save failed'); }
    finally { setSaving(false); }
  };

  // ── Blackouts ─────────────────────────────────────────────────────────────

  const addBlackout = async () => {
    if (!blackoutForm.start_date || !blackoutForm.end_date || !blackoutForm.reason)
      return toast.error('All fields required');
    try {
      await groundsAPI.addBlackout(selSeries, blackoutForm);
      toast.success('Blackout added');
      setShowBlackoutModal(false);
      setBlackoutForm({ start_date: '', end_date: '', reason: '' });
      loadBlackouts(selSeries);
    } catch (err) { toast.error(err.response?.data?.error || 'Failed'); }
  };

  const deleteBlackout = async () => {
    try {
      await groundsAPI.deleteBlackout(deletingBlackout);
      toast.success('Blackout removed');
      setDeletingBlackout(null);
      loadBlackouts(selSeries);
    } catch { toast.error('Failed to delete blackout'); }
  };

  // ── Computed metrics ──────────────────────────────────────────────────────

  const seriesObj      = seriesList.find(s => s.id === selSeries);
  const hasNoDivisions = seriesObj?.structure === 'no_divisions' || seriesObj?.structure === 'groups_only';

  // Pool-aware per-division match cards
  const divMatchCards = divisions.map(d => {
    const { total, breakdown } = computeMatchesForDivision(d, poolCountsMap);
    return { ...d, totalMatches: total, poolBreakdown: breakdown };
  });

  const totalMatches = hasNoDivisions
    ? parseInt(manualGames['total'] || 0, 10)
    : divMatchCards.reduce((a, d) => a + d.totalMatches, 0);

  // Build a lookup: groundId -> [{ teamShort, blockedDates:[] }]
  const bookingsByGround = {};
  groundBookings.forEach(b => {
    const gid = b.value?.ground_id;
    if (!gid) return;
    if (!bookingsByGround[gid]) bookingsByGround[gid] = [];
    bookingsByGround[gid].push({
      teamShort:    b.value?.team_short || '?',
      teamId:       b.value?.team_id,
      gamesCount:   b.value?.games_count || 1,
      blockedDates: Array.isArray(b.value?.blocked_dates) ? b.value.blocked_dates : [],
      note:         b.value?.note || '',
    });
  });

  const groundSlotsCalc = () => {
    let total = 0;
    const notes = [];
    const perGround = [];
    grounds.forEach(g => {
      const map          = groundDateMaps[g.id] || {};
      const rule         = groundRules.find(r => r.value?.ground_id === g.id);
      const maxPerDay    = rule?.value?.max_games_per_day
        ? parseInt(rule.value.max_games_per_day, 10)
        : (g.max_concurrent || 1);
      const allowedTimes = rule?.value?.allowed_times || null;
      const divRestrict  = rule?.value?.division_restriction || null;
      const gndBookings  = bookingsByGround[g.id] || [];
      const allBlockedDates = gndBookings.flatMap(bk => bk.blockedDates);

      // ── Count raw calendar slots (available dates × maxPerDay, minus blackouts) ──
      let calendarDates = 0;
      let calendarSlots = 0;
      Object.entries(map).forEach(([dateStr, status]) => {
        if (status !== 'available') return;
        const isBO = blackouts.some(b =>
          dateStr >= b.start_date?.split('T')[0] && dateStr <= b.end_date?.split('T')[0]
        );
        const isTeamBlocked = allBlockedDates.includes(dateStr);
        if (!isBO && !isTeamBlocked) { calendarSlots += maxPerDay; calendarDates++; }
      });

      // ── Apply conditions override logic ──
      // C15 Home Ground Assignments: if this ground has bookings with games_count,
      // cap the effective slots to the total booked games — the ground is reserved
      // exclusively for those home games and cannot exceed that allocation.
      const bookedGames = gndBookings.reduce((s, bk) => s + (bk.gamesCount || 0), 0);
      const hasHomeAssignment = gndBookings.length > 0 && bookedGames > 0;

      let slots, slotSource;
      if (hasHomeAssignment) {
        // C15 override: slots = exactly what's booked, capped by calendar availability
        slots = Math.min(bookedGames, calendarSlots);
        slotSource = 'c15';  // "Home Ground Assignment"
      } else {
        // No C15 — use full calendar count
        slots = calendarSlots;
        slotSource = 'calendar';
      }

      perGround.push({
        id: g.id, name: g.name,
        maxPerDay, allowedTimes, divRestrict,
        premierPriority:   rule?.value?.premier_priority || false,
        minGamesOtherDivs: rule?.value?.min_games_other_divs || 0,
        availDates: calendarDates,
        calendarSlots,
        slots,
        slotSource,
        bookedGames,
        bookings: gndBookings,
        hasData: Object.keys(map).length > 0,
      });

      if (slots > 0) { total += slots; notes.push(`${g.name}: ${slots}`); }
    });
    return { total, notes, perGround };
  };

  const { total: groundSlots, notes: slotNotes, perGround: groundSlotRows } = groundSlotsCalc();
  const missingGames = totalMatches - groundSlots;
  const hasData      = totalMatches > 0;

  const availCount   = Object.values(currentMap).filter(v => v === 'available').length;
  const blockedCount = Object.values(currentMap).filter(v => v === 'blocked').length;
  const selGroundObj = grounds.find(g => g.id === selGround);

  // ── No-series gate ────────────────────────────────────────────────────────

  if (!selSeries) {
    return (
      <div>
        <PageHeader icon="◷" title="Ground Availability"
          subtitle="Set available and blocked dates per series per ground." />
        <div className="card" style={{ padding: 48, textAlign: 'center', maxWidth: 480, margin: '0 auto' }}>
          <div style={{ fontSize: 40, marginBottom: 14 }}>📅</div>
          <div style={{ fontSize: 17, fontWeight: 700, color: 'var(--text)', marginBottom: 8 }}>Select a Series to Begin</div>
          <div style={{ fontSize: 13, color: 'var(--mid)', marginBottom: 24, lineHeight: 1.6 }}>
            Ground availability is configured per series. The calendar will show only the play days defined in
            <strong> Conditions → C1 Game Day Rules</strong> for that series.
          </div>
          <select className="input select" value={selSeries}
            onChange={e => setSelSeries(e.target.value)}
            style={{ maxWidth: 320, margin: '0 auto', display: 'block' }}>
            <option value="">— Choose a series —</option>
            {seriesList.map(s => <option key={s.id} value={s.id}>{s.name} ({s.season_year})</option>)}
          </select>
        </div>
      </div>
    );
  }

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div>
      <PageHeader icon="◷" title="Ground Availability"
        subtitle="Set available and blocked dates per series per ground."
        actions={canEdit() && (
          <button className="btn btn-primary" onClick={saveAll} disabled={saving || !dirty}>
            {saving ? <><Spinner size={14} /> Saving…</> : dirty ? '● Save Changes' : '✓ Saved'}
          </button>
        )}
      />

      {/* ── Selector bar ── */}
      <div className="card" style={{ padding: '12px 16px', marginBottom: 16, display: 'flex', gap: 16, alignItems: 'flex-end', flexWrap: 'wrap' }}>
        <Field label="Series" style={{ flex: '0 0 220px', marginBottom: 0 }}>
          <select className="input select" value={selSeries}
            onChange={e => { setSelSeries(e.target.value); setSelDivision(''); }}>
            <option value="">— Choose series —</option>
            {seriesList.map(s => <option key={s.id} value={s.id}>{s.name} ({s.season_year})</option>)}
          </select>
        </Field>
        {!hasNoDivisions && divisions.length > 0 && (
          <Field label="Division" note="Optional filter" style={{ flex: '0 0 180px', marginBottom: 0 }}>
            <select className="input select" value={selDivision} onChange={e => setSelDivision(e.target.value)}>
              <option value="">All Divisions</option>
              {divisions.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
            </select>
          </Field>
        )}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, paddingBottom: 2 }}>
          <span style={{ fontSize: 11, color: 'var(--mid)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Play Days:</span>
          {playDayLabels.length > 0
            ? playDayLabels.map(d => (
                <span key={d} style={{ background: 'rgba(0,200,150,.18)', color: 'var(--accent)', borderRadius: 5, padding: '3px 10px', fontSize: 12, fontWeight: 700 }}>{d}</span>
              ))
            : <span style={{ color: 'var(--red)', fontSize: 12, fontStyle: 'italic' }}>Not set — configure in Conditions → C1</span>
          }
        </div>
      </div>

      {/* ── Manual games for no-division series ── */}
      {hasNoDivisions && (
        <div className="card" style={{ padding: 16, marginBottom: 16 }}>
          <SectionTitle color="var(--gold)">Match Count</SectionTitle>
          <p style={{ fontSize: 12, color: 'var(--mid)', marginBottom: 10 }}>
            This series has no divisions. Enter the total expected matches to enable slot calculations.
          </p>
          <Field label="Total Matches in Series">
            <input className="input" type="number" min="0" style={{ maxWidth: 160 }}
              value={manualGames['total'] || ''}
              onChange={e => setManualGames(p => ({ ...p, total: e.target.value }))}
              placeholder="e.g. 48" />
          </Field>
        </div>
      )}

      {/* ── Summary cards ── */}
      {seriesObj && (
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 16 }}>
          {!hasNoDivisions && divMatchCards.map(d => (
            <StatCard key={d.id}
              label={d.name}
              value={d.totalMatches}
              sub={`${d.team_count ?? 0} teams · ${d.games_per_team} games/team`}
              accent="var(--blue)"
              breakdown={d.poolBreakdown}
              note={d.poolBreakdown
                ? d.poolBreakdown.some(p => p.note?.includes('odd'))
                  ? 'Odd pool: (g−1) league rounds + seeded final game. Rank 1 gets bye.'
                  : null
                : null}
            />
          ))}

          <StatCard
            label="Total Matches"
            value={totalMatches || '—'}
            sub={hasNoDivisions ? 'Manually entered' : `${divisions.length} division${divisions.length !== 1 ? 's' : ''}`}
            accent="var(--text)"
            note={hasNoDivisions ? null : 'Pool-aware: even=(g×t)/2 · odd=(g×t−1)/2'}
          />

          <StatCard
            label="Ground Slots Available"
            value={groundSlots || '—'}
            sub={`${grounds.filter(g => Object.values(groundDateMaps[g.id] || {}).some(s => s === 'available')).length} grounds with availability`}
            accent="var(--gold)"
            note={`Conditions-adjusted: C10 home assignments cap their grounds; others use available dates × games/day.`}
          />

          <StatCard
            label={!hasData ? 'Slot Balance' : missingGames > 0 ? '⚠ Slots Deficit' : missingGames < 0 ? '✓ Surplus Slots' : '✓ Perfect Balance'}
            value={!hasData ? '—' : Math.abs(missingGames)}
            sub={
              !hasData
                ? 'Set match counts & availability to compute'
                : missingGames > 0
                  ? `Need ${missingGames} more slots — add availability or reduce matches`
                  : missingGames < 0
                    ? `${Math.abs(missingGames)} extra slots — schedule has room`
                    : 'Exact fit — schedule can proceed'
            }
            accent={!hasData ? 'var(--dim)' : missingGames > 0 ? 'var(--red)' : 'var(--accent)'}
            highlight={hasData}
            highlightColor={missingGames > 0 ? 'var(--red)' : 'var(--accent)'}
            note="Total Matches − Ground Slots. Must be ≤ 0 (green) before running the schedule generator."
          />
        </div>
      )}

      {/* ── Per-ground slot breakdown card ── */}
      {seriesObj && groundSlotRows.length > 0 && (
        <div className="card" style={{ padding: '14px 18px', marginBottom: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--mid)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
              Ground Slot Breakdown
            </div>
            <div style={{ fontSize: 10, color: 'var(--dim)', fontStyle: 'italic' }}>
              From Conditions → C5 Ground Rules &amp; C10 Home Ground Assignments
            </div>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {groundSlotRows.map(g => {
              const blockedTotal = g.bookings.reduce((s, b) => s + (b.blockedDates?.length || 0), 0);
              const notConfigured = !g.hasData;
              const isCapped = g.slotSource === 'c15';
              const calendarExcess = isCapped ? g.calendarSlots - g.slots : 0;
              return (
                <div key={g.id}
                  onClick={() => setSelGround(g.id)}
                  title={`Click to view ${g.name}`}
                  style={{
                    padding: '10px 14px', borderRadius: 8, cursor: 'pointer',
                    minWidth: 160, flex: '1 1 160px', maxWidth: 220,
                    background: selGround === g.id ? 'rgba(0,200,150,.08)' : 'var(--surface)',
                    border: `1.5px solid ${selGround === g.id ? 'var(--accent)' : notConfigured ? 'var(--border)' : isCapped ? 'rgba(155,109,255,.35)' : 'rgba(0,200,150,.25)'}`,
                    transition: 'all .12s',
                  }}>
                  {/* Ground name */}
                  <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text)', marginBottom: 6, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span>{g.name}</span>
                    {notConfigured && <span style={{ fontSize: 9, color: 'var(--dim)', fontStyle: 'italic' }}>not set</span>}
                  </div>
                  {/* Slot count row */}
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 4, marginBottom: 4 }}>
                    <span style={{ fontSize: 22, fontWeight: 800, color: notConfigured ? 'var(--dim)' : isCapped ? 'var(--purple)' : 'var(--gold)', fontFamily: "'Roboto Mono', monospace", lineHeight: 1 }}>
                      {notConfigured ? '—' : g.slots}
                    </span>
                    <span style={{ fontSize: 10, color: 'var(--mid)' }}>slots</span>
                  </div>
                  {/* Source label */}
                  {!notConfigured && (
                    <div style={{ fontSize: 9, color: isCapped ? 'var(--purple)' : 'var(--dim)', marginBottom: 6, fontStyle: isCapped ? 'normal' : 'italic', fontWeight: isCapped ? 600 : 400 }}>
                      {isCapped
                        ? `📋 Capped by C10 · ${g.availDates} date${g.availDates !== 1 ? 's' : ''} available${calendarExcess > 0 ? ` (${calendarExcess} unused)` : ''}`
                        : `${g.availDates} date${g.availDates !== 1 ? 's' : ''} × ${g.maxPerDay}/day`}
                    </div>
                  )}
                  {/* Tags */}
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                    {g.maxPerDay > 1 && (
                      <span style={{ fontSize: 9, background: 'rgba(100,149,237,.15)', color: 'var(--blue)', borderRadius: 3, padding: '1px 6px', fontWeight: 600 }}>
                        Max {g.maxPerDay}/day
                      </span>
                    )}
                    {g.allowedTimes && (
                      <span style={{ fontSize: 9, background: 'rgba(0,200,150,.12)', color: 'var(--accent)', borderRadius: 3, padding: '1px 6px' }}>
                        🕐 {g.allowedTimes}
                      </span>
                    )}
                    {g.premierPriority && (
                      <span style={{ fontSize: 9, background: 'rgba(245,166,35,.15)', color: 'var(--gold)', borderRadius: 3, padding: '1px 6px', fontWeight: 600 }}>
                        ★ Premier priority
                      </span>
                    )}
                    {g.premierPriority && g.minGamesOtherDivs > 0 && (
                      <span style={{ fontSize: 9, background: 'rgba(155,109,255,.15)', color: 'var(--purple)', borderRadius: 3, padding: '1px 6px', fontWeight: 600 }}>
                        Min {g.minGamesOtherDivs}/div
                      </span>
                    )}
                    {!g.premierPriority && g.divRestrict && g.divRestrict !== 'none' && (
                      <span style={{ fontSize: 9, background: 'rgba(245,166,35,.15)', color: 'var(--gold)', borderRadius: 3, padding: '1px 6px', fontWeight: 600 }}>
                        {g.divRestrict === 'premier_only' ? '★ Premier only' : g.divRestrict}
                      </span>
                    )}
                    {g.bookings.map((bk, i) => (
                      <span key={i} style={{ fontSize: 9, background: 'rgba(155,109,255,.15)', color: 'var(--purple)', borderRadius: 3, padding: '1px 6px', fontWeight: 700 }}>
                        🏠 {bk.teamShort} ×{bk.gamesCount}
                      </span>
                    ))}
                    {blockedTotal > 0 && (
                      <span style={{ fontSize: 9, background: 'rgba(239,68,68,.15)', color: 'var(--red)', borderRadius: 3, padding: '1px 6px', fontWeight: 600 }}>
                        {blockedTotal} blocked
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ── Main grid ── */}
      <div style={{ display: 'grid', gridTemplateColumns: '280px 1fr', gap: 20 }}>

        {/* Sidebar */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>

          {/* Ground + apply-to-all */}
          <div className="card" style={{ padding: 16 }}>
            <Field label="Ground">
              <select className="input select" value={selGround}
                onChange={e => setSelGround(e.target.value)}>
                <option value="">Select ground…</option>
                {grounds.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
              </select>
            </Field>

            {/* Apply-to-all toggle */}
            <div onClick={() => setApplyToAll(p => !p)}
              style={{
                display: 'flex', alignItems: 'center', gap: 10, padding: '9px 11px',
                borderRadius: 7, marginTop: 4, cursor: 'pointer',
                background: applyToAll ? 'rgba(245,166,35,.1)' : 'var(--surface)',
                border: `1px solid ${applyToAll ? 'rgba(245,166,35,.5)' : 'var(--border)'}`,
                transition: 'all .15s',
              }}>
              <div style={{ width: 34, height: 20, borderRadius: 10, flexShrink: 0, position: 'relative', background: applyToAll ? 'var(--gold)' : 'var(--border)', transition: 'background .15s' }}>
                <div style={{ position: 'absolute', top: 4, left: applyToAll ? 17 : 4, width: 12, height: 12, borderRadius: '50%', background: '#fff', transition: 'left .15s' }} />
              </div>
              <div>
                <div style={{ fontSize: 12, fontWeight: 700, color: applyToAll ? 'var(--gold)' : 'var(--text)' }}>Apply to All Grounds</div>
                <div style={{ fontSize: 10, color: 'var(--dim)', marginTop: 1 }}>
                  {applyToAll ? `Affects all ${grounds.length} grounds` : 'Selected ground only'}
                </div>
              </div>
            </div>
            {applyToAll && (
              <div style={{ marginTop: 8, padding: '7px 10px', borderRadius: 6, background: 'rgba(245,166,35,.06)', border: '1px solid rgba(245,166,35,.2)', fontSize: 11, color: 'var(--gold)', lineHeight: 1.6 }}>
                💡 Mark all grounds first, then adjust individual grounds for exceptions.
              </div>
            )}

            <hr className="divider" />

            {/* Bulk actions */}
            <SectionTitle color="var(--gold)">Bulk Actions</SectionTitle>
            {playDayLabels.length === 0 ? (
              <div style={{ fontSize: 11, color: 'var(--dim)', fontStyle: 'italic', padding: '4px 0' }}>
                Set play days in Conditions → C1 to enable bulk actions.
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                {playDayLabels.map(day => (
                  <button key={`av-${day}`} className="btn btn-ghost"
                    style={{ fontSize: 12, justifyContent: 'flex-start' }}
                    onClick={() => bulkMark(DAY_TO_DOW[day], 'available')}>
                    ✓ Mark All {day}s Available
                  </button>
                ))}
                {playDayLabels.map(day => (
                  <button key={`bl-${day}`} className="btn btn-ghost"
                    style={{ fontSize: 12, justifyContent: 'flex-start' }}
                    onClick={() => bulkMark(DAY_TO_DOW[day], 'blocked')}>
                    ✗ Block All {day}s
                  </button>
                ))}
                <button className="btn btn-ghost"
                  style={{ fontSize: 12, justifyContent: 'flex-start' }}
                  onClick={() => bulkMarkAllDates('available')}>
                  ✓ Mark All Play Dates Available
                </button>
                <button className="btn btn-ghost"
                  style={{ fontSize: 12, color: 'var(--red)', justifyContent: 'flex-start' }}
                  onClick={clearAll}>
                  ✗ Clear All
                </button>
              </div>
            )}

            {/* Quick stats for current ground */}
            {playDates.length > 0 && selGround && (
              <>
                <hr className="divider" />
                <div style={{ fontSize: 11, color: 'var(--mid)', lineHeight: 2 }}>
                  <div>Available: <span style={{ color: 'var(--accent)', fontWeight: 700 }}>{availCount}</span></div>
                  <div>Blocked: <span style={{ color: 'var(--red)', fontWeight: 700 }}>{blockedCount}</span></div>
                  <div>Unmarked: <span style={{ color: 'var(--dim)', fontWeight: 700 }}>{playDates.length - availCount - blockedCount}</span></div>
                </div>
              </>
            )}
          </div>

          {/* Ground Rules (sidebar — refresh button) */}
          <div className="card" style={{ padding: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
              <SectionTitle color="var(--blue)">Ground Rules</SectionTitle>
              <button className="btn btn-ghost btn-sm"
                onClick={() => selSeries && loadAllConditions(selSeries)}
                disabled={rulesLoading} title="Refresh">
                {rulesLoading ? <Spinner size={12} /> : '↻ Refresh'}
              </button>
            </div>
            {rulesLoading ? (
              <div style={{ display: 'flex', justifyContent: 'center', padding: 12 }}><Spinner size={16} /></div>
            ) : groundRules.length === 0 && teamGroundLimits.length === 0 ? (
              <div style={{ fontSize: 11, color: 'var(--dim)', lineHeight: 1.6 }}>No ground rules defined.</div>
            ) : (
              <>
                {groundRules.map(r => {
                  const gnd = grounds.find(g => g.id === r.value?.ground_id);
                  const v   = r.value || {};
                  return (
                    <div key={r.id} style={{ padding: '7px 10px', borderRadius: 6, marginBottom: 5, background: 'rgba(100,149,237,.08)', border: '1px solid rgba(100,149,237,.18)', fontSize: 11 }}>
                      <span style={{ fontWeight: 700, color: 'var(--blue)' }}>{gnd?.name || '?'}</span>
                      {v.max_games_per_day && <span style={{ color: 'var(--mid)', marginLeft: 6 }}>· Max {v.max_games_per_day}/day</span>}
                      {v.division_restriction && v.division_restriction !== 'none' && (
                        <span style={{ color: 'var(--gold)', marginLeft: 6 }}>
                          · {v.division_restriction === 'premier_only' ? '★ Premier only' : v.division_restriction}
                        </span>
                      )}
                    </div>
                  );
                })}
                {teamGroundLimits.map(r => {
                  const gnd = grounds.find(g => g.id === r.value?.ground_id);
                  const v   = r.value || {};
                  return (
                    <div key={r.id} style={{ padding: '7px 10px', borderRadius: 6, marginBottom: 5, background: 'rgba(155,109,255,.08)', border: '1px solid rgba(155,109,255,.18)', fontSize: 11 }}>
                      <span style={{ fontWeight: 700, color: 'var(--purple)' }}>
                        {v.team_short || (divisions.find(d => d.id === v.division_id)?.name) || 'Teams'}
                      </span>
                      <span style={{ color: 'var(--mid)', marginLeft: 6 }}>@ {gnd?.name || '?'}</span>
                      <span style={{ color: 'var(--purple)', marginLeft: 6 }}>· max {v.max_times}×/season</span>
                    </div>
                  );
                })}
              </>
            )}
            <div style={{ marginTop: 8, fontSize: 10, color: 'var(--dim)', lineHeight: 1.5 }}>
              Edit in <strong>Conditions → C5 Ground Rules</strong> or <strong>C8 Team–Ground Limits</strong>
            </div>
          </div>

          {/* Global Blackouts */}
          <div className="card" style={{ padding: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
              <SectionTitle color="var(--red)">Global Blackouts</SectionTitle>
              {canEdit() && <button className="btn btn-ghost btn-sm" onClick={() => setShowBlackoutModal(true)}>+ Add</button>}
            </div>
            {blackouts.length === 0 ? (
              <div style={{ fontSize: 11, color: 'var(--dim)', padding: '4px 0' }}>No blackouts set</div>
            ) : blackouts.map(b => (
              <div key={b.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 0', borderBottom: '1px solid rgba(255,255,255,.05)' }}>
                <div>
                  <span className="tag tag-red" style={{ fontSize: 10 }}>
                    {new Date(b.start_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                    {b.start_date !== b.end_date && ` – ${new Date(b.end_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`}
                  </span>
                  <div style={{ fontSize: 10, color: 'var(--mid)', marginTop: 2 }}>{b.reason}</div>
                </div>
                {canEdit() && (
                  <button onClick={() => setDeletingBlackout(b.id)}
                    style={{ background: 'none', border: 'none', color: 'var(--red)', cursor: 'pointer', fontSize: 12, padding: '2px 6px' }}>✕</button>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Calendar */}
        <div className="card" style={{ padding: 20 }}>
          {!selGround ? (
            <div style={{ textAlign: 'center', padding: 60, color: 'var(--mid)', fontSize: 13 }}>
              Select a ground to manage its availability.
            </div>
          ) : playDates.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 60, color: 'var(--mid)', fontSize: 13, lineHeight: 1.8 }}>
              {playDayLabels.length === 0
                ? <span>No play days configured.<br /><strong>Conditions → C1 Game Day Rules</strong></span>
                : 'No dates found — check series start/end dates.'}
            </div>
          ) : (
            <>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                <SectionTitle>
                  {applyToAll
                    ? <span style={{ color: 'var(--gold)' }}>All Grounds — {seriesObj?.name}</span>
                    : <>{selGroundObj?.name} <span style={{ color: 'var(--mid)', fontWeight: 400 }}>— {seriesObj?.name}</span></>
                  }
                </SectionTitle>
                <div style={{ display: 'flex', gap: 10, fontSize: 11, alignItems: 'center' }}>
                  {['available','blocked','restricted','none'].map(s => (
                    <span key={s} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                      <div style={{ width: 10, height: 10, borderRadius: 3, background: STATUS_COLORS[s].text, opacity: s === 'none' ? 0.3 : 0.7 }} />
                      <span style={{ color: 'var(--mid)', textTransform: 'capitalize' }}>{s}</span>
                    </span>
                  ))}
                  {canEdit() && <span style={{ color: 'var(--dim)', fontSize: 10, marginLeft: 4 }}>Click to cycle</span>}
                </div>
              </div>

              {loadingGround ? (
                <div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}><Spinner size={28} /></div>
              ) : (
                <>
                  {(() => {
                    const months = {};
                    playDates.forEach(d => {
                      const key = d.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
                      if (!months[key]) months[key] = [];
                      months[key].push(d);
                    });
                    return Object.entries(months).map(([month, dates]) => (
                      <div key={month} style={{ marginBottom: 24 }}>
                        <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--mid)', letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 8 }}>{month}</div>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                          {dates.map(d => {
                            const key        = fmt(d);
                            const status     = currentMap[key] || 'none';
                            const col        = STATUS_COLORS[status];
                            const isBlackout = blackouts.some(b =>
                              key >= b.start_date?.split('T')[0] && key <= b.end_date?.split('T')[0]
                            );
                            const dow = d.getDay();
                            // Ground bookings for this ground on this date
                            const gndBookings = bookingsByGround[selGround] || [];
                            // Teams with this date explicitly blocked
                            const blockedTeams = gndBookings.filter(bk => bk.blockedDates.includes(key));
                            // Teams booked (home games) at this ground — show on available dates
                            const bookedTeams  = gndBookings.filter(bk => !bk.blockedDates.includes(key) && status === 'available');
                            const hasBookings  = bookedTeams.length > 0;
                            const hasBlocked   = blockedTeams.length > 0;
                            return (
                              <div key={key}
                                onClick={() => !isBlackout && toggleDate(key)}
                                title={
                                  isBlackout ? 'Global blackout'
                                  : hasBlocked ? `Blocked for: ${blockedTeams.map(b => b.teamShort).join(', ')} — ${blockedTeams[0]?.note || ''}`
                                  : hasBookings ? `Reserved for: ${bookedTeams.map(b => `${b.teamShort} (${b.gamesCount} games)`).join(', ')}`
                                  : `${status} — click to change`
                                }
                                style={{
                                  width: 64, padding: '8px 4px 4px', borderRadius: 7, textAlign: 'center',
                                  background: isBlackout
                                    ? 'repeating-linear-gradient(45deg, rgba(239,68,68,.14) 0 6px, rgba(239,68,68,.04) 6px 12px)'
                                    : col.bg,
                                  // Global blackout gets a distinct dashed red border + thicker width so
                                  // it stands apart from regular available/blocked/reserved states.
                                  border: isBlackout
                                    ? '2px dashed rgba(239,68,68,.75)'
                                    : `1.5px solid ${
                                        hasBlocked ? 'rgba(239,68,68,.5)'
                                        : hasBookings ? 'rgba(0,200,150,.6)'
                                        : col.border
                                      }`,
                                  boxShadow: isBlackout ? 'inset 0 0 0 1px rgba(239,68,68,.12)' : undefined,
                                  cursor: isBlackout ? 'not-allowed' : canEdit() ? 'pointer' : 'default',
                                  opacity: isBlackout ? 0.85 : 1, transition: 'all .12s',
                                }}>
                                <div style={{ fontSize: 9, color: 'var(--mid)', fontWeight: 600 }}>{DOW_LABEL[dow]}</div>
                                <div style={{ fontSize: 14, fontWeight: 800, color: isBlackout ? 'var(--red)' : col.text, marginTop: 2 }}>{d.getDate()}</div>
                                <div style={{ fontSize: 9, marginTop: 2, color: isBlackout ? 'var(--red)' : col.text, opacity: 0.8, textTransform: 'uppercase' }}>
                                  {isBlackout ? 'blackout' : status === 'none' ? '—' : status}
                                </div>
                                {/* Team booking badges */}
                                {hasBlocked && blockedTeams.map(bk => (
                                  <div key={bk.teamShort} style={{
                                    marginTop: 3, fontSize: 8, fontWeight: 700, lineHeight: 1.2,
                                    background: 'rgba(239,68,68,.18)', color: 'var(--red)',
                                    borderRadius: 3, padding: '1px 3px',
                                  }}>✗ {bk.teamShort}</div>
                                ))}
                                {!hasBlocked && hasBookings && bookedTeams.map(bk => (
                                  <div key={bk.teamShort} style={{
                                    marginTop: 3, fontSize: 8, fontWeight: 700, lineHeight: 1.2,
                                    background: 'rgba(0,200,150,.2)', color: 'var(--accent)',
                                    borderRadius: 3, padding: '1px 3px',
                                  }}>{bk.teamShort}</div>
                                ))}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    ));
                  })()}

                  {dirty && canEdit() && (
                    <div style={{ marginTop: 16, padding: '10px 14px', background: 'rgba(245,166,35,.1)', border: '1px solid rgba(245,166,35,.3)', borderRadius: 7, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ fontSize: 12, color: 'var(--gold)' }}>
                        ● Unsaved changes{applyToAll ? ` — will save all ${grounds.length} grounds` : ''}
                      </span>
                      <button className="btn btn-primary btn-sm" onClick={saveAll} disabled={saving}>
                        {saving ? 'Saving…' : 'Save Now'}
                      </button>
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </div>
      </div>

      {/* Add Blackout Modal */}
      {showBlackoutModal && (
        <Modal title="Add Global Blackout" onClose={() => setShowBlackoutModal(false)} width={440}>
          <Field label="Start Date" required>
            <SingleDatePicker value={blackoutForm.start_date}
              onChange={v => setBlackoutForm(p => ({ ...p, start_date: v }))}
              width="100%" />
          </Field>
          <Field label="End Date" required>
            <SingleDatePicker value={blackoutForm.end_date}
              onChange={v => setBlackoutForm(p => ({ ...p, end_date: v }))}
              minDate={blackoutForm.start_date ? new Date(blackoutForm.start_date + 'T12:00:00') : null}
              width="100%" />
          </Field>
          <Field label="Reason" required>
            <input className="input" placeholder="e.g. Memorial Day Weekend" value={blackoutForm.reason}
              onChange={e => setBlackoutForm(p => ({ ...p, reason: e.target.value }))} />
          </Field>
          <hr className="divider" />
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button className="btn btn-ghost" onClick={() => setShowBlackoutModal(false)}>Cancel</button>
            <button className="btn btn-danger" onClick={addBlackout}>+ Add Blackout</button>
          </div>
        </Modal>
      )}

      {deletingBlackout && (
        <ConfirmDialog danger
          message="Remove this global blackout? Dates will no longer be blocked across all grounds."
          onConfirm={deleteBlackout}
          onCancel={() => setDeletingBlackout(null)}
        />
      )}
    </div>
  );
}
